/**
 * Typed API client for /api/partner-portal/*.
 *
 * All endpoints derive the partner's identity from the authenticated session
 * server-side. The frontend NEVER sends a partner_id — that would be a
 * security bug. See spec hard rules #4 + #5.
 */

import api from '../services/apiClient';

// ─── Domain type unions (mirror src/partner-portal/types.ts on backend) ───
export type EngagementStatus =
  | 'planning'
  | 'in_progress'
  | 'review'
  | 'delivered'
  | 'blocked';

export type AssessmentRequestStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'withdrawn';

export type ObligationType =
  | 'msa'
  | 'insurance'
  | 'iso_cert'
  | 'dpa'
  | 'practitioner_currency';

export type ObligationStatus = 'green' | 'amber' | 'red';

export type DocumentType =
  | 'working_paper'
  | 'deliverable'
  | 'evidence'
  | 'other';

export type DocumentReviewStatus = 'draft' | 'in_review' | 'approved';

export type PartnerTier =
  | 'platinum'
  | 'gold'
  | 'silver'
  | 'launchpad'
  | 'pending';

/** Human-readable label for a tier badge. 'pending' renders as
 *  "Tier pending" so demos don't show every partner as Launchpad until
 *  the survey/SLA/incident schema lands. */
export function partnerTierLabel(t: PartnerTier): string {
  switch (t) {
    case 'platinum': return 'Platinum';
    case 'gold':     return 'Gold';
    case 'silver':   return 'Silver';
    case 'launchpad':return 'Launchpad';
    case 'pending':  return 'Tier pending';
  }
}

// ─── Dashboard ─────────────────────────────────────────────────────────────
export interface DashboardTaskDue {
  id: string;
  title: string;
  framework: string;
  status: EngagementStatus;
  target_date: string;
}

export interface Dashboard {
  active_engagements_count: number;
  pending_requests_count: number;
  this_month_earnings: Record<string, number>;
  tasks_due_this_week: DashboardTaskDue[];
}

// ─── Engagements ───────────────────────────────────────────────────────────
export interface EngagementSummary {
  id: string;
  title: string;
  framework: string;
  status: EngagementStatus;
  start_date: string | null;
  target_date: string | null;
  delivered_date: string | null;
  progress_pct: number;
  client_tenant_id: string;
}

export interface EngagementStaffingRow {
  staffing_id?: string;
  practitioner_id: string;
  practitioner_name: string | null;
  role_on_engagement: string;
  assigned_at: string;
}

export interface EngagementDetail extends EngagementSummary {
  completed_milestones: number;
  total_milestones: number;
  assessment_request_id: string | null;
  staffing: EngagementStaffingRow[];
  ai_spend?: {
    total_usd: number;
    total_tokens_input: number;
    total_tokens_output: number;
    run_count: number;
    runs_completed: number;
    runs_failed: number;
  };
}

// ─── Requests ──────────────────────────────────────────────────────────────
export interface AssessmentRequestRow {
  id: string;
  framework: string;
  scope_summary: string | null;
  budget_currency: string | null;
  budget_amount: number | null;
  requested_timeline: string | null;
  status: AssessmentRequestStatus;
  decline_reason: string | null;
  responded_at: string | null;
  created_at: string;
  client_tenant_id: string;
}

// ─── Practitioners ─────────────────────────────────────────────────────────
export interface PractitionerCertRow {
  id: string;
  cert_code: string;
  cert_name: string;
  issued_at: string;
  expires_at: string | null;
}

export interface PractitionerRow {
  id: string;
  full_name: string;
  role: string;
  email: string;
  phone: string | null;
  years_experience: number | null;
  joined_at: string;
  is_active: boolean;
  utilisation_pct: number;
  certifications: PractitionerCertRow[];
}

// ─── Messages ──────────────────────────────────────────────────────────────
export interface MessageAttachment {
  fileName: string;
  fileUrl: string;
  sha256?: string;
}

export interface MessageRow {
  id: string;
  engagement_id: string;
  from_user_id: string;
  body: string;
  attachments: MessageAttachment[];
  read_at: string | null;
  created_at: string;
}

// ─── Documents ─────────────────────────────────────────────────────────────
export interface DocumentRow {
  id: string;
  engagement_id: string;
  file_name: string;
  file_url: string;
  sha256: string;
  size_bytes: number | null;
  mime_type: string | null;
  document_type: DocumentType;
  review_status: DocumentReviewStatus;
  uploaded_by_user_id: string;
  uploaded_at: string;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
}

// ─── Earnings ──────────────────────────────────────────────────────────────
export interface EarningsAttribution {
  id: string;
  engagement_id: string;
  client_paid_amount: number;
  client_paid_currency: string;
  partner_share_amount: number;
  period_month: string;
  payout_status: 'pending' | 'paid';
  paid_at: string | null;
}

export interface Earnings {
  period_from: string;
  period_to: string;
  grand_total_partner_share: number;
  grand_total_by_currency: Record<string, number>;
  by_period: Record<string, Record<string, number>>;
  by_engagement: Record<string, Record<string, number>>;
  attributions: EarningsAttribution[];
}

// ─── Compliance status ─────────────────────────────────────────────────────
export interface ObligationRow {
  obligation_type: ObligationType;
  expires_at: string | null;
  document_url: string | null;
  last_uploaded_at: string | null;
  status: ObligationStatus;
}

export interface ComplianceStatus {
  overall: ObligationStatus;
  obligations: ObligationRow[];
}

// ─── Notifications ─────────────────────────────────────────────────────────
export type NotificationKind =
  | 'finding_raised'
  | 'action_due_soon'
  | 'action_overdue'
  | 'document_approved'
  | 'document_rejected'
  | 'request_accepted'
  | 'request_declined'
  | 'message_received'
  | 'obligation_scoped'
  | string;

export interface PartnerNotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  link_url: string | null;
  engagement_id: string | null;
  read_at: string | null;
  created_at: string;
}

// ─── Engagement gap-assessment (new in 2026-05) ────────────────────────────
export type AssessmentStatus =
  | 'not_assessed'
  | 'compliant'
  | 'partial'
  | 'non_compliant'
  | 'not_applicable';

export interface EngagementAssessmentRow {
  id: string;
  engagement_id: string;
  obligation_id: string;
  obligation_code: string | null;
  obligation_title: string | null;
  obligation_regulation: string | null;
  obligation_jurisdiction: string | null;
  obligation_impact: string | null;
  status: AssessmentStatus;
  notes: string | null;
  evidence_doc_ids: string[];
  scoped_at: string;
  updated_at: string;
}

export type FindingSeverity = 'low' | 'medium' | 'high' | 'critical';
export type FindingLikelihood = 'low' | 'medium' | 'high';

export interface PartnerFindingRow {
  id: string;
  engagement_id: string;
  obligation_id: string | null;
  title: string;
  severity: FindingSeverity;
  likelihood: FindingLikelihood | null;
  description: string | null;
  recommendation: string | null;
  evidence_doc_ids: string[];
  created_at: string;
  updated_at: string;
}

export type PartnerActionStatus = 'open' | 'in_progress' | 'closed';

export interface PartnerActionPlanRow {
  id: string;
  engagement_id: string;
  finding_id: string | null;
  title: string;
  owner: string;
  due_date: string;
  status: PartnerActionStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ─── AI suggestions (Phase 2) ──────────────────────────────────────────
export type AiAgentType =
  | 'policy_reader'
  | 'gap_detector'
  | 'cross_mapper'
  | 'risk_scorer'
  | 'recommender';

export type AiReviewStatus =
  | 'pending'
  | 'accepted'
  | 'modified'
  | 'rejected'
  | 'info_requested';

export interface AiEvidenceLocation {
  document_title?: string;
  document_id?: string | null;
  /** DMS-side blob URL; when present, the EvidenceCitation chip renders
   *  as a click-through that opens the source doc at the cited page. */
  file_url?: string | null;
  document_language?: 'en' | 'ar' | string;
  clause_id?: string;
  heading?: string | null;
  page_number?: number | null;
  section?: string | null;
  candidate_topics?: string[];
}

export interface AiSuggestionRow {
  id: string;
  agent_run_id: string;
  engagement_id: string;
  obligation_id: string | null;
  agent_type: AiAgentType;
  suggested_status: string | null;
  suggested_severity: string | null;
  suggested_likelihood: string | null;
  confidence_score: number;
  reasoning_en: string | null;
  reasoning_ar: string | null;
  evidence_locations: AiEvidenceLocation[];
  original_quotes: string[];
  has_conflict: boolean;
  conflict_details: unknown;
  cross_mapping_targets: unknown[];
  review_status: AiReviewStatus;
  reviewer_id: string | null;
  reviewer_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface AiAgentRunSummary {
  id: string;
  engagement_id: string;
  triggered_by: 'upload' | 'assessment_start' | 'manual' | 'cross_mapping';
  started_at: string;
  completed_at: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  llm_route: string;
  frameworks_analyzed: string[];
  total_tokens_input: number;
  total_tokens_output: number;
  total_cost_usd: number;
  error_message: string | null;
}

export interface PolicyReaderRunResult {
  run_id: string;
  suggestions_created: number;
  status: 'completed' | 'failed';
  error_message: string | null;
}

// ─── Tenant profile (market profile, language, active frameworks) ─────────
export type MarketProfile = 'us' | 'arab' | 'global';

export interface TenantProfileResponse {
  tenant_id: string;
  name: string;
  slug: string;
  archetype: string;
  market_profile: MarketProfile | null;
  active_frameworks: string[];
  data_residency_region: string | null;
  default_language: 'en' | 'ar' | string;
  ui_direction: 'ltr' | 'rtl';
  needs_onboarding: boolean;
}

export interface TenantProfileUpdateBody {
  market_profile?: MarketProfile;
  active_frameworks?: string[];
  data_residency_region?: string;
  default_language?: 'en' | 'ar';
  ui_direction?: 'ltr' | 'rtl';
}

// ─── Feature flags ────────────────────────────────────────────────────────
export type FeatureFlagMap = Record<string, unknown>;

// ─── Obligations catalog (read-only reference) ─────────────────────────────
export type ObligationImpact = 'low' | 'medium' | 'high' | 'critical' | string;

export interface ObligationCatalogItem {
  id: string;
  code: string | null;
  title: string;
  regulation: string;
  jurisdiction: string;
  lastUpdateDate?: string;
  recentlyUpdated?: boolean;
  status?: string;
  confidenceScore?: number;
  description?: string;
  impactLevel?: ObligationImpact;
  // Phase 1 substrate fields
  frameworkCode?: string | null;
  frameworkVersion?: string | null;
  titleAr?: string | null;
  descriptionAr?: string | null;
  sourceLanguage?: 'en' | 'ar' | string;
}

// ─── Profile ───────────────────────────────────────────────────────────────
export interface PartnerPortalProfile {
  tenant_id: string;
  name: string;
  name_ar: string | null;
  logo_url: string | null;
  archetype: 'client' | 'consulting_firm' | 'audit_firm' | 'regulator';
  tier: PartnerTier;
  status: 'active' | 'paused' | 'terminated';
  region_codes: string[];
  specialisations: string[];
  currency: string;
  revenue_share_pct: number;
  joined_at: string;
  msa_signed_at: string | null;
  msa_expires_at: string | null;
  data_residency: string | null;
  incomplete_fields: string[];
  is_publicly_visible: boolean;
}

// ─── AI suggestion review ───────────────────────────────────────────────────

/** Reject-reason allow-list. Mirrors REJECT_REASONS on the backend. */
export const REJECT_REASONS = [
  'incorrect_interpretation',
  'missing_context',
  'wrong_severity',
  'duplicate_finding',
  'framework_not_applicable',
  'other',
] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

// ─── API surface ───────────────────────────────────────────────────────────
const BASE = '/api/partner-portal';

export const partnerPortalApi = {
  getDashboard: (): Promise<Dashboard> => api.get(`${BASE}/dashboard`),

  listEngagements: (params: { status?: EngagementStatus; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    const qs = q.toString();
    return api.get<EngagementSummary[]>(
      `${BASE}/engagements${qs ? `?${qs}` : ''}`,
    );
  },
  getEngagement: (id: string): Promise<EngagementDetail> =>
    api.get(`${BASE}/engagements/${encodeURIComponent(id)}`),
  updateEngagement: (
    id: string,
    body: Partial<{
      status: EngagementStatus;
      start_date: string | null;
      target_date: string | null;
      delivered_date: string | null;
      completed_milestones: number;
      total_milestones: number;
      title: string;
    }>,
  ): Promise<EngagementDetail> =>
    api.patch(`${BASE}/engagements/${encodeURIComponent(id)}`, body),
  addStaffing: (
    engagementId: string,
    body: { practitioner_id: string; role_on_engagement: string },
  ): Promise<{
    staffing_id: string;
    practitioner_id: string;
    role_on_engagement: string;
    assigned_at: string;
  }> =>
    api.post(
      `${BASE}/engagements/${encodeURIComponent(engagementId)}/staffing`,
      body,
    ),
  releaseStaffing: (engagementId: string, staffingId: string): Promise<void> =>
    api.delete<void>(
      `${BASE}/engagements/${encodeURIComponent(engagementId)}/staffing/${encodeURIComponent(staffingId)}`,
    ),

  // Per-engagement AI overrides (Phase 3 §17).
  getEngagementAiSettings: (
    engagementId: string,
  ): Promise<{
    engagement_id: string;
    overrides: Record<string, unknown>;
    overridable_keys: string[];
  }> =>
    api.get(
      `${BASE}/engagements/${encodeURIComponent(engagementId)}/ai-settings`,
    ),
  setEngagementAiSetting: (
    engagementId: string,
    flagKey: string,
    value: unknown | null,
  ): Promise<{
    engagement_id: string;
    flag_key: string;
    value?: unknown;
    cleared?: boolean;
  }> =>
    api.put(
      `${BASE}/engagements/${encodeURIComponent(engagementId)}/ai-settings/${encodeURIComponent(flagKey)}`,
      { value },
    ),

  listRequests: (status?: AssessmentRequestStatus) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return api.get<AssessmentRequestRow[]>(`${BASE}/requests${qs}`);
  },
  acceptRequest: (id: string): Promise<{ request_id: string; engagement_id: string }> =>
    api.post(`${BASE}/requests/${encodeURIComponent(id)}/accept`, {}),
  declineRequest: (id: string, reason: string): Promise<{ id: string; status: string }> =>
    api.post(`${BASE}/requests/${encodeURIComponent(id)}/decline`, { reason }),
  withdrawRequest: (id: string): Promise<{ id: string; status: string }> =>
    api.post(`${BASE}/requests/${encodeURIComponent(id)}/withdraw`, {}),

  listPractitioners: (): Promise<PractitionerRow[]> => api.get(`${BASE}/practitioners`),
  createPractitioner: (body: {
    full_name: string;
    role: string;
    email: string;
    phone?: string;
    years_experience?: number;
  }): Promise<{ id: string }> => api.post(`${BASE}/practitioners`, body),
  updatePractitioner: (id: string, body: Partial<{
    full_name: string;
    role: string;
    phone: string | null;
    years_experience: number | null;
    is_active: boolean;
  }>): Promise<{ id: string }> =>
    api.patch(`${BASE}/practitioners/${encodeURIComponent(id)}`, body),
  addCertification: (
    practitionerId: string,
    body: {
      cert_code: string;
      cert_name: string;
      issued_at: string;
      expires_at?: string;
      document_url?: string;
    },
  ): Promise<{ id: string }> =>
    api.post(
      `${BASE}/practitioners/${encodeURIComponent(practitionerId)}/certifications`,
      body,
    ),
  deletePractitioner: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/practitioners/${encodeURIComponent(id)}`),
  deleteCertification: (practitionerId: string, certId: string): Promise<void> =>
    api.delete<void>(
      `${BASE}/practitioners/${encodeURIComponent(practitionerId)}/certifications/${encodeURIComponent(certId)}`,
    ),

  listMessages: (engagementId?: string): Promise<MessageRow[]> => {
    const qs = engagementId
      ? `?engagement_id=${encodeURIComponent(engagementId)}`
      : '';
    return api.get(`${BASE}/messages${qs}`);
  },
  sendMessage: (body: {
    engagement_id: string;
    body: string;
    attachments?: MessageAttachment[];
  }): Promise<{ id: string }> => api.post(`${BASE}/messages`, body),
  markMessageRead: (id: string): Promise<{ id: string; read_at: string }> =>
    api.post(`${BASE}/messages/${encodeURIComponent(id)}/read`, {}),

  listDocuments: (engagementId?: string): Promise<DocumentRow[]> => {
    const qs = engagementId
      ? `?engagement_id=${encodeURIComponent(engagementId)}`
      : '';
    return api.get(`${BASE}/documents${qs}`);
  },
  createDocument: (body: {
    engagement_id: string;
    file_name: string;
    file_url: string;
    sha256: string;
    size_bytes?: number;
    mime_type?: string;
    document_type?: DocumentType;
    /** Phase 2: partner-controlled visibility. Default 'shared'. */
    visibility?: 'shared' | 'partner_only';
  }): Promise<{ id: string }> => api.post(`${BASE}/documents`, body),
  submitDocumentReview: (id: string) =>
    api.post<{ id: string; review_status: DocumentReviewStatus }>(
      `${BASE}/documents/${encodeURIComponent(id)}/submit-review`,
      {},
    ),
  approveDocument: (id: string) =>
    api.post<{ id: string; review_status: DocumentReviewStatus }>(
      `${BASE}/documents/${encodeURIComponent(id)}/approve`,
      {},
    ),
  rejectDocument: (id: string) =>
    api.post<{ id: string; review_status: DocumentReviewStatus }>(
      `${BASE}/documents/${encodeURIComponent(id)}/reject`,
      {},
    ),
  deleteDocument: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/documents/${encodeURIComponent(id)}`),

  getEarnings: (params: { period_from?: string; period_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.period_from) q.set('period_from', params.period_from);
    if (params.period_to) q.set('period_to', params.period_to);
    const qs = q.toString();
    return api.get<Earnings>(`${BASE}/earnings${qs ? `?${qs}` : ''}`);
  },

  getComplianceStatus: (): Promise<ComplianceStatus> =>
    api.get(`${BASE}/compliance-status`),
  uploadObligation: (
    type: ObligationType,
    body: { document_url: string; expires_at: string },
  ): Promise<{ obligation_type: ObligationType; status: ObligationStatus }> =>
    api.post(`${BASE}/obligations/${encodeURIComponent(type)}/upload`, body),

  /**
   * Regulatory obligations catalog — read-only reference data shared with
   * the admin app. Partners use this to scope an engagement against CBE /
   * FRA / GDPR / ISO clauses. Lives under /obligations (NOT under
   * /api/partner-portal) but the backend's PartnerArchetypeGuard allows
   * partners to read it as a regulated exception (see guard allow-list).
   */
  listObligations: (): Promise<ObligationCatalogItem[]> =>
    // Partner-scoped: server filters by tenant.active_frameworks.
    api.get<ObligationCatalogItem[]>(`${BASE}/obligations`),
  listActiveFrameworks: (): Promise<Array<{
    code: string;
    version: string | null;
    regulation: string;
    jurisdiction: string;
    obligation_count: number;
  }>> => api.get(`${BASE}/frameworks`),

  // ── Tenant market profile ──
  getTenantProfile: (): Promise<TenantProfileResponse> =>
    api.get<TenantProfileResponse>(`${BASE}/tenant-profile`),
  updateTenantProfile: (body: TenantProfileUpdateBody): Promise<TenantProfileResponse> =>
    api.patch(`${BASE}/tenant-profile`, body),

  // ── Feature flags ──
  getFeatureFlags: (): Promise<FeatureFlagMap> =>
    api.get<FeatureFlagMap>(`${BASE}/feature-flags`),
  setFeatureFlag: (key: string, value: unknown): Promise<{ key: string; value: unknown }> =>
    api.put(`${BASE}/feature-flags/${encodeURIComponent(key)}`, { value }),

  // ── AI suggestions ──
  listAiSuggestions: (params: {
    engagement_id?: string;
    status?: AiReviewStatus;
  } = {}): Promise<AiSuggestionRow[]> => {
    const q = new URLSearchParams();
    if (params.engagement_id) q.set('engagement_id', params.engagement_id);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return api.get<AiSuggestionRow[]>(
      `${BASE}/ai-suggestions${qs ? `?${qs}` : ''}`,
    );
  },
  getAiAgentRun: (id: string): Promise<AiAgentRunSummary> =>
    api.get<AiAgentRunSummary>(
      `${BASE}/ai-suggestions/runs/${encodeURIComponent(id)}`,
    ),
  runPolicyReader: (body: {
    engagement_id: string;
    document_title: string;
    document_text: string;
    is_banking_content?: boolean;
  }): Promise<PolicyReaderRunResult> =>
    api.post(`${BASE}/ai-suggestions/policy-reader/run`, body),
  runAiChain: (body: {
    engagement_id: string;
    document_title: string;
    document_text: string;
    is_banking_content?: boolean;
  }): Promise<{ job_id: string; status: 'queued' }> =>
    api.post(`${BASE}/ai-suggestions/run-chain`, body),
  getAiChainJobStatus: (
    jobId: string,
  ): Promise<{
    id: string;
    state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
    progress: number | object;
    attemptsMade: number;
    failedReason: string | null;
    returnvalue: {
      runId: string;
      status: 'completed' | 'failed';
      errorMessage?: string;
      clauses_extracted: number;
      gaps_detected: number;
      cross_mappings: number;
      risks_scored: number;
      recommendations: number;
      total_cost_usd: number;
    } | null;
  }> =>
    api.get(`${BASE}/ai-suggestions/run-chain/jobs/${encodeURIComponent(jobId)}`),
  analyzeDocument: (documentId: string): Promise<{
    run_id: string;
    status: 'completed' | 'failed';
    error_message: string | null;
    clauses_extracted: number;
    gaps_detected: number;
    cross_mappings: number;
    risks_scored: number;
    recommendations: number;
    total_cost_usd: number;
    extraction: {
      method: 'pdf' | 'text' | 'stub';
      size_bytes: number;
      mime_type: string;
      warnings: string[];
    };
  }> =>
    api.post(`${BASE}/ai-suggestions/documents/${encodeURIComponent(documentId)}/analyze`, {}),
  listConflicts: (engagementId: string): Promise<AiSuggestionRow[]> =>
    api.get<AiSuggestionRow[]>(
      `${BASE}/ai-suggestions/conflicts?engagement_id=${encodeURIComponent(engagementId)}`,
    ),
  rankConflictSources: (body: {
    engagement_id: string;
    obligation_id: string;
  }): Promise<{
    obligation_id: string;
    ranking: Array<{ suggestion_id: string; rank: number; reasoning: string }>;
    confident: boolean;
    summary: string | null;
    model: string;
    cost_usd: number;
  }> => api.post(`${BASE}/ai-suggestions/conflicts/rank-sources`, body),
  resolveConflict: (body: {
    engagement_id: string;
    obligation_id: string;
    resolution: 'choose' | 'ask_client';
    winner_suggestion_id?: string;
    message?: string;
  }): Promise<{
    resolution: 'choose' | 'ask_client';
    winner_suggestion_id?: string;
    created_finding_id?: string | null;
    auto_propagated?: Array<{ suggestion_id: string; finding_id: string | null }>;
    rejected_count?: number;
    message_sent?: boolean;
    suggestions_updated?: number;
  }> => api.post(`${BASE}/ai-suggestions/conflicts/resolve`, body),

  // ── Cross-mapping admin ──
  listObligationMappings: (): Promise<Array<{
    id: string;
    source_obligation_id: string;
    target_obligation_id: string;
    mapping_strength: 'equivalent' | 'partial' | 'related';
    confidence: number;
    source: 'official' | 'community' | 'ai_suggested';
    notes: string | null;
    created_at: string;
    source_obligation: { id: string; code: string | null; title: string; regulation: string; framework_code: string | null } | null;
    target_obligation: { id: string; code: string | null; title: string; regulation: string; framework_code: string | null } | null;
  }>> => api.get(`${BASE}/cross-mapping`),
  createObligationMapping: (body: {
    source_obligation_id: string;
    target_obligation_id: string;
    mapping_strength: 'equivalent' | 'partial' | 'related';
    confidence: number;
    source?: 'official' | 'community' | 'ai_suggested';
    notes?: string;
  }): Promise<unknown> => api.post(`${BASE}/cross-mapping`, body),
  updateObligationMapping: (id: string, body: Partial<{
    mapping_strength: 'equivalent' | 'partial' | 'related';
    confidence: number;
    source: 'official' | 'community' | 'ai_suggested';
    notes: string | null;
  }>): Promise<unknown> =>
    api.patch(`${BASE}/cross-mapping/${encodeURIComponent(id)}`, body),
  deleteObligationMapping: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/cross-mapping/${encodeURIComponent(id)}`),
  listMappingsByObligation: (
    obligationId: string,
  ): Promise<
    Array<{
      id: string;
      source_obligation_id: string;
      target_obligation_id: string;
      mapping_strength: 'equivalent' | 'partial' | 'related';
      confidence: number;
      source: 'official' | 'community' | 'ai_suggested';
      notes: string | null;
      direction: 'outgoing' | 'incoming';
      source_obligation: { id: string; code: string | null; title: string; framework_code: string | null } | null;
      target_obligation: { id: string; code: string | null; title: string; framework_code: string | null } | null;
    }>
  > =>
    api.get(
      `${BASE}/cross-mapping/by-obligation/${encodeURIComponent(obligationId)}`,
    ),
  suggestCrossMappings: (body: {
    source_framework: string;
    target_frameworks?: string[];
    max_source_obligations?: number;
    candidates_per_obligation?: number;
    min_confidence?: number;
  }): Promise<{
    source_framework: string;
    obligations_processed: number;
    mappings_created: number;
    skipped_existing: number;
    total_cost_usd: number;
    errors: string[];
  }> => api.post(`${BASE}/cross-mapping/suggest`, body),
  acceptAiSuggestion: (
    id: string,
    body: { overrides?: Record<string, unknown>; notes?: string } = {},
  ): Promise<
    AiSuggestionRow & {
      created_finding_id: string | null;
      // Phase 3: equivalent high-confidence cross-mappings auto-accepted
      // alongside this one (empty unless ai_cross_mapping_auto_propagate).
      auto_propagated?: Array<{ suggestion_id: string; finding_id: string | null }>;
    }
  > => api.post(`${BASE}/ai-suggestions/${encodeURIComponent(id)}/accept`, body),
  modifyAiSuggestion: (
    id: string,
    body: { overrides: Record<string, unknown>; notes?: string },
  ): Promise<AiSuggestionRow> =>
    api.post(`${BASE}/ai-suggestions/${encodeURIComponent(id)}/modify`, body),
  rejectAiSuggestion: (
    id: string,
    body: { reason: RejectReason; notes?: string },
  ): Promise<AiSuggestionRow> =>
    api.post(`${BASE}/ai-suggestions/${encodeURIComponent(id)}/reject`, body),
  requestInfoOnSuggestion: (id: string, notes: string): Promise<AiSuggestionRow> =>
    api.post(`${BASE}/ai-suggestions/${encodeURIComponent(id)}/request-info`, { notes }),

  // ── Assessments ──
  listAssessments: (engagementId: string): Promise<EngagementAssessmentRow[]> =>
    api.get<EngagementAssessmentRow[]>(
      `${BASE}/assessments?engagement_id=${encodeURIComponent(engagementId)}`,
    ),
  scopeAssessment: (body: {
    engagement_id: string;
    obligation_id: string;
  }): Promise<EngagementAssessmentRow> => api.post(`${BASE}/assessments`, body),
  updateAssessment: (
    id: string,
    body: { status?: AssessmentStatus; notes?: string | null; evidence_doc_ids?: string[] },
  ): Promise<EngagementAssessmentRow> =>
    api.patch(`${BASE}/assessments/${encodeURIComponent(id)}`, body),
  unscopeAssessment: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/assessments/${encodeURIComponent(id)}`),

  // ── Findings ──
  listFindings: (engagementId: string): Promise<PartnerFindingRow[]> =>
    api.get<PartnerFindingRow[]>(
      `${BASE}/findings?engagement_id=${encodeURIComponent(engagementId)}`,
    ),
  createFinding: (body: {
    engagement_id: string;
    obligation_id?: string | null;
    title: string;
    severity: FindingSeverity;
    likelihood?: FindingLikelihood | null;
    description?: string | null;
    recommendation?: string | null;
    evidence_doc_ids?: string[];
  }): Promise<PartnerFindingRow> => api.post(`${BASE}/findings`, body),
  updateFinding: (
    id: string,
    body: Partial<{
      title: string;
      severity: FindingSeverity;
      likelihood: FindingLikelihood | null;
      obligation_id: string | null;
      description: string | null;
      recommendation: string | null;
      evidence_doc_ids: string[];
    }>,
  ): Promise<PartnerFindingRow> =>
    api.patch(`${BASE}/findings/${encodeURIComponent(id)}`, body),
  deleteFinding: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/findings/${encodeURIComponent(id)}`),

  // ── Action plans ──
  listActionPlans: (engagementId: string): Promise<PartnerActionPlanRow[]> =>
    api.get<PartnerActionPlanRow[]>(
      `${BASE}/action-plans?engagement_id=${encodeURIComponent(engagementId)}`,
    ),
  createActionPlan: (body: {
    engagement_id: string;
    finding_id?: string | null;
    title: string;
    owner: string;
    due_date: string;
    status?: PartnerActionStatus;
    notes?: string | null;
  }): Promise<PartnerActionPlanRow> => api.post(`${BASE}/action-plans`, body),
  updateActionPlan: (
    id: string,
    body: Partial<{
      title: string;
      owner: string;
      due_date: string;
      status: PartnerActionStatus;
      notes: string | null;
      finding_id: string | null;
    }>,
  ): Promise<PartnerActionPlanRow> =>
    api.patch(`${BASE}/action-plans/${encodeURIComponent(id)}`, body),
  deleteActionPlan: (id: string): Promise<void> =>
    api.delete<void>(`${BASE}/action-plans/${encodeURIComponent(id)}`),

  // ── Notifications ──
  listNotifications: (unreadOnly?: boolean): Promise<PartnerNotificationRow[]> =>
    api.get<PartnerNotificationRow[]>(
      `${BASE}/notifications${unreadOnly ? '?unread_only=true' : ''}`,
    ),
  getUnreadNotificationCount: (): Promise<{ count: number }> =>
    api.get<{ count: number }>(`${BASE}/notifications/unread-count`),
  markNotificationRead: (id: string): Promise<PartnerNotificationRow> =>
    api.post(`${BASE}/notifications/${encodeURIComponent(id)}/read`, {}),
  markAllNotificationsRead: (): Promise<void> =>
    api.post<void>(`${BASE}/notifications/read-all`, {}),

  getProfile: (): Promise<PartnerPortalProfile> => api.get(`${BASE}/profile`),
  updateProfile: (body: Partial<{
    name: string;
    name_ar: string;
    region_codes: string[];
    specialisations: string[];
    currency: string;
    msa_signed_at: string;
    msa_expires_at: string;
    data_residency: string;
  }>): Promise<{ updated: boolean }> => api.patch(`${BASE}/profile`, body),
  uploadLogo: (logo_url: string): Promise<{ logo_url: string }> =>
    api.post(`${BASE}/profile/logo`, { logo_url }),

  // ── Platform admin (gated by PLATFORM_ADMIN_TENANT_IDS on backend) ──
  listPilotCohort: (): Promise<
    Array<{
      tenant_id: string;
      name: string;
      slug: string;
      archetype: string;
      market_profile: 'us' | 'arab' | 'global' | null;
      is_active: boolean;
      pilot_cohort: boolean;
    }>
  > => api.get(`${BASE}/admin/pilot-cohort`),

  setPilotCohort: (
    tenantId: string,
    enabled: boolean,
  ): Promise<{ tenant_id: string; pilot_cohort: boolean }> =>
    api.put(`${BASE}/admin/pilot-cohort/${encodeURIComponent(tenantId)}`, { enabled }),

  getAiMetrics: (): Promise<{
    window_days: number;
    suggestions_per_day: Array<{ day: string; profile: string; cnt: number }>;
    review_distribution: Array<{ agent_type: string; review_status: string; cnt: number }>;
    avg_confidence_per_framework: Array<{
      framework_code: string;
      avg_confidence: number;
      accepted_count: number;
    }>;
    spend_per_tenant: Array<{
      tenant_id: string;
      tenant_name: string;
      profile: string;
      spend_usd: number;
      run_count: number;
    }>;
    cost_per_agent: Array<{ agent_type: string; cost_usd: number }>;
    mapping_coverage: Array<{
      framework_code: string;
      total: number;
      mapped: number;
      coverage_pct: number;
    }>;
    reject_reasons: Array<{ reason: string; count: number }>;
    per_market: Array<{
      profile: string;
      tenants: number;
      suggestions: number;
      accepted: number;
    }>;
    slos?: {
      window_days: number;
      sync: { total: number; failed: number; p95_ms: number };
      async: { total: number; failed: number; p95_ms: number };
      error_rate_total: number;
    };
    cache?: { hits: number; misses: number; hitRate: number };
  }> => api.get(`${BASE}/admin/ai-metrics`),
};
